import { useState } from 'react'
import { cn } from '@/lib/utils'
import { InfoPopover } from '@/components/InfoPopover'
import { MetricDeltas } from '@/components/sales-report/components/MetricDeltas'
import type { Utilisation } from '../types'
import { MONO, fmt, monthLabel } from './format'
import { MonthBars } from './MonthBars'

type Comparison = { current: number; prevMonth: number | null; prevYear: number | null; prevYearLabel: string; prevLabel: string }

const CARD = 'min-w-0 rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900'
const SLATE = '#8A97AD'
const CHAMP = '#AE9056'

/** Credit utilisation — the standing line made available vs drawn down. The
 *  headline is the AVERAGE of each month's utilisation across the range (over the
 *  months that had a credit line); hover a bar to read that single month. */
export function UtilisationCard({
  util,
  currency,
  months,
  comparison,
}: {
  util: Utilisation
  currency: string
  months: string[]
  comparison: Comparison
}) {
  const [hov, setHov] = useState<number | null>(null)
  const max = Math.max(1, ...util.madeAvailable, ...util.used)
  // Average over months that actually had a credit line (skip pre-programme zeros).
  const activeMonths = util.madeAvailable.map((v, i) => (v > 0 ? i : -1)).filter((i) => i >= 0)
  const avgOf = (arr: number[]) => (activeMonths.length ? Math.round(activeMonths.reduce((s, i) => s + arr[i], 0) / activeMonths.length) : 0)
  const made = hov != null ? util.madeAvailable[hov] : avgOf(util.madeAvailable)
  const used = hov != null ? util.used[hov] : avgOf(util.used)
  const pct = hov != null ? util.usedPct[hov] : avgOf(util.usedPct)
  const multi = months.length > 1
  const span = multi ? `${monthLabel(months[0])} – ${monthLabel(months[months.length - 1])}` : monthLabel(months[0])
  const caption = hov != null ? monthLabel(months[hov]) : multi ? `monthly avg · ${span}` : span

  return (
    <div className={CARD}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="inline-flex items-center gap-1 text-sm font-semibold text-stone-800 dark:text-stone-100">
          Credit utilisation
          <InfoPopover term="creditMadeAvailable" />
        </h3>
        <span className="text-[11px] text-stone-400 dark:text-stone-500">made available vs used · {caption}</span>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-x-8 gap-y-4">
        <div>
          <p className={cn('text-4xl font-semibold tracking-tight', MONO)}>{pct}%</p>
          <p className="inline-flex items-center gap-1 text-[11px] text-stone-400 dark:text-stone-500">
            {hov == null && multi ? 'avg used of available' : 'used of available'}
            <InfoPopover term="pcCreditUsed" />
          </p>
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-3">
          <Fig label="Made available" value={fmt(made, currency)} dot={SLATE} />
          <Fig label="Used on orders" value={fmt(used, currency)} dot={CHAMP} />
        </div>
      </div>
      <MetricDeltas current={comparison.current} prevMonth={comparison.prevMonth} prevYear={comparison.prevYear} prevYearLabel={comparison.prevYearLabel} prevLabel={comparison.prevLabel} currency={currency} unit="percent" />

      {/* Made (light) vs Used (champagne) per month — hover to read the month's totals */}
      <div className="mt-5">
        <MonthBars months={months} onHover={setHov} isActive={(i) => hov === i}>
          {(i) => {
            const on = hov === i
            return (
              <div className="flex w-full items-end justify-center gap-0.5" style={{ height: 76 }}>
                <div className="min-w-0 flex-1 rounded-t" style={{ height: Math.round((util.madeAvailable[i] / max) * 76), backgroundColor: SLATE, opacity: on ? 0.55 : 0.28 }} />
                <div className="min-w-0 flex-1 rounded-t" style={{ height: Math.round((util.used[i] / max) * 76), backgroundColor: CHAMP, opacity: on ? 1 : 0.65 }} />
              </div>
            )
          }}
        </MonthBars>
      </div>
      <p className="mt-3 text-[11px] text-stone-400 dark:text-stone-500">
        Headline is the average of each month&rsquo;s utilisation across the range; hover a bar for that month. Made available is a standing balance (capacity, not cash granted), so it&rsquo;s averaged, not summed; used can exceed it when a clinic draws beyond its ceiling.
      </p>
    </div>
  )
}

function Fig({ label, value, dot, info }: { label: string; value: string; dot?: string; info?: string }) {
  return (
    <div>
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] text-stone-400 dark:text-stone-500">
        {dot && <span className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: dot }} />}
        {label}
        {info && <InfoPopover term={info} />}
      </span>
      <p className={cn('mt-1 text-sm font-medium', MONO)}>{value}</p>
    </div>
  )
}
